import React, { useState, useRef, useEffect, useCallback } from 'react';
import flvjs from 'flv.js';
import streamingConfig from '../config/streaming-config';
import { playSrsWebRtc } from '../utils/srsWebRtcPlayer';

/**
 * 指挥台低延迟播放器：默认 WebRTC，失败自动回落 HTTP-FLV
 */
const OptimizedVideoPlayer = ({
  streamKey,
  streamUrl, // 兼容旧用法：直接传 FLV/HLS URL
  memberName,
  protocol = 'webrtc',
  fallbackProtocol = 'flv',
  onError,
  onSuccess,
  onLoadingChange,
  style = {}
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentProtocol, setCurrentProtocol] = useState(protocol);
  const [isMuted, setIsMuted] = useState(true);
  const [statusText, setStatusText] = useState('');

  const videoRef = useRef(null);
  const flvRef = useRef(null);
  const webrtcCloseRef = useRef(null);
  const fallbackTriedRef = useRef(false);
  const key = (streamKey || memberName || '').replace(/\s+/g, '');

  const cleanupPlayers = useCallback(() => {
    if (flvRef.current) {
      try {
        flvRef.current.pause();
        flvRef.current.unload();
        flvRef.current.detachMediaElement();
        flvRef.current.destroy();
      } catch (e) {
        /* ignore */
      }
      flvRef.current = null;
    }
    if (webrtcCloseRef.current) {
      try {
        webrtcCloseRef.current();
      } catch (e) {
        /* ignore */
      }
      webrtcCloseRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const initFLV = useCallback((url) => {
    if (!flvjs.isSupported()) {
      setError('浏览器不支持 FLV');
      return false;
    }
    const playUrl = url || streamingConfig.getFlvUrl(key);
    try {
      const flvPlayer = flvjs.createPlayer(
        {
          type: 'flv',
          url: playUrl,
          isLive: true,
          hasAudio: true,
          hasVideo: true
        },
        {
          enableStashBuffer: false,
          stashInitialSize: 128,
          enableWorker: false,
          lazyLoad: false,
          autoCleanupSourceBuffer: true
        }
      );
      flvRef.current = flvPlayer;
      flvPlayer.attachMediaElement(videoRef.current);
      flvPlayer.load();
      flvPlayer.play().catch(() => {});

      flvPlayer.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
        console.error('FLV播放错误:', errorType, errorDetail);
        setError('FLV 播放失败');
        if (onError) onError('flv', { errorType, errorDetail });
      });

      const markOk = () => {
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        setStatusText('FLV 兜底播放中（约1-2秒延时）');
        if (onSuccess) onSuccess('flv');
        if (onLoadingChange) onLoadingChange(false);
      };

      if (videoRef.current) {
        videoRef.current.addEventListener('playing', markOk, { once: true });
        videoRef.current.addEventListener('canplay', markOk, { once: true });
      }
      return true;
    } catch (e) {
      console.error('初始化FLV失败:', e);
      setError('FLV 初始化失败');
      return false;
    }
  }, [key, onError, onSuccess, onLoadingChange]);

  const fallbackToFlv = useCallback(
    (reason) => {
      if (fallbackTriedRef.current || fallbackProtocol !== 'flv') {
        setIsLoading(false);
        setError(reason || '播放失败');
        if (onLoadingChange) onLoadingChange(false);
        return;
      }
      fallbackTriedRef.current = true;
      console.warn('WebRTC 失败，回落 FLV:', reason);
      setStatusText('WebRTC 不可用，切换 FLV…');
      setCurrentProtocol('flv');
      cleanupPlayers();
      const ok = initFLV(streamUrl);
      if (!ok) {
        setIsLoading(false);
        setError(reason || 'WebRTC/FLV 均失败');
        if (onLoadingChange) onLoadingChange(false);
      }
    },
    [fallbackProtocol, cleanupPlayers, initFLV, streamUrl, onLoadingChange]
  );

  const initWebRTC = useCallback(async () => {
    if (!key) {
      setError('缺少 streamKey');
      return false;
    }
    try {
      setStatusText('正在连接 WebRTC…');
      const { stream, close } = await playSrsWebRtc(key);
      webrtcCloseRef.current = close;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setIsLoading(false);
      setIsPlaying(true);
      setError(null);
      setStatusText('WebRTC 低延迟播放中');
      if (onSuccess) onSuccess('webrtc');
      if (onLoadingChange) onLoadingChange(false);

      // 若数秒仍无视频轨道帧，视为失败并回落
      setTimeout(() => {
        const v = videoRef.current;
        if (v && v.srcObject && v.videoWidth === 0 && !fallbackTriedRef.current) {
          fallbackToFlv('WebRTC 无画面');
        }
      }, 5000);
      return true;
    } catch (e) {
      console.error('WebRTC 初始化失败:', e);
      fallbackToFlv(e.message || 'WebRTC 失败');
      return false;
    }
  }, [key, onSuccess, onLoadingChange, fallbackToFlv]);

  const start = useCallback(async () => {
    cleanupPlayers();
    fallbackTriedRef.current = false;
    setIsLoading(true);
    setIsPlaying(false);
    setError(null);
    if (onLoadingChange) onLoadingChange(true);

    const want = protocol || 'webrtc';
    setCurrentProtocol(want);

    if (want === 'webrtc') {
      await initWebRTC();
      return;
    }
    if (want === 'flv') {
      const ok = initFLV(streamUrl);
      if (!ok) {
        setIsLoading(false);
        if (onLoadingChange) onLoadingChange(false);
      }
      return;
    }
    setError(`不支持的协议: ${want}`);
    setIsLoading(false);
  }, [protocol, streamUrl, cleanupPlayers, initWebRTC, initFLV, onLoadingChange]);

  useEffect(() => {
    start();
    return () => cleanupPlayers();
    // streamKey/protocol/streamUrl 变化时重连即可
  }, [key, protocol, streamUrl]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const reload = () => start();

  const switchProtocol = (next) => {
    setCurrentProtocol(next);
    fallbackTriedRef.current = next === 'flv';
    cleanupPlayers();
    setIsLoading(true);
    if (next === 'webrtc') {
      initWebRTC();
    } else {
      initFLV(streamUrl);
    }
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000'
        }}
        muted={isMuted}
        playsInline
        autoPlay
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {isLoading && !isPlaying && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#fff',
            fontSize: 14,
            textAlign: 'center',
            zIndex: 10
          }}
        >
          <div>正在加载 {currentProtocol.toUpperCase()}…</div>
          {statusText ? <div style={{ marginTop: 6, opacity: 0.8 }}>{statusText}</div> : null}
        </div>
      )}

      {error && !isPlaying && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#fff',
            fontSize: 14,
            textAlign: 'center',
            zIndex: 10,
            background: 'rgba(180,0,0,0.85)',
            padding: 12,
            borderRadius: 6
          }}
        >
          <div>{error}</div>
          <button
            type="button"
            onClick={reload}
            style={{ marginTop: 8, padding: '4px 10px', cursor: 'pointer' }}
          >
            重试
          </button>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 10,
          background: 'rgba(0,0,0,0.7)',
          color: '#fff',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 12,
          zIndex: 11
        }}
      >
        {currentProtocol.toUpperCase()}
        {statusText ? ` · ${statusText}` : ''}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          display: 'flex',
          gap: 8,
          zIndex: 11
        }}
      >
        <button type="button" onClick={toggleMute} style={{ cursor: 'pointer' }}>
          {isMuted ? '取消静音' : '静音'}
        </button>
        <select
          value={currentProtocol}
          onChange={(e) => switchProtocol(e.target.value)}
          style={{ cursor: 'pointer' }}
        >
          <option value="webrtc">WebRTC（低延迟）</option>
          <option value="flv">FLV（兜底）</option>
        </select>
        <button type="button" onClick={reload} style={{ cursor: 'pointer' }}>
          刷新
        </button>
      </div>
    </div>
  );
};

export default OptimizedVideoPlayer;
